import styled from '@emotion/styled';
import {
  formatTimeDuration,
  timeDurationToMilliseconds,
} from '@racemap/utilities/formattings/times';
import { isNoBoolean } from '@racemap/utilities/functions/utils';
import { isDefined, isNotNull } from '@racemap/utilities/functions/validation';
import {
  type SelectProps as AntdSelectProps,
  Checkbox,
  type CheckboxChangeEvent,
  Flex,
  Input,
  type InputProps,
  Segmented,
  Select,
  Space,
  Switch,
  Typography,
} from 'antd';
import type { TextAreaProps } from 'antd/es/input';
import type { DefaultOptionType } from 'antd/es/select';
import type { SwitchChangeEventHandler } from 'antd/es/switch';
import type { Immutable } from 'immer';
import { isArray } from 'lodash';
import React, { type CSSProperties, type FocusEventHandler, useEffect, useState } from 'react';
import { toastError } from './utils/Utils';

const { Text } = Typography;

function createLabel(label?: React.ReactNode, required?: boolean, hint?: React.ReactNode) {
  if (label == null) return null;
  switch (typeof label) {
    case 'string':
    case 'boolean':
    case 'number':
      return (
        <Space size="small" style={{ marginBottom: '0.5rem' }} direction="horizontal">
          <Text strong>
            {label} {required && <sup>*</sup>}
          </Text>
          {hint}
        </Space>
      );
    case 'object':
      return (
        <>
          {label}
          {hint}
        </>
      );
    default:
      return null;
  }
}

// workaround, in the popover links seems to get blocket. that component takes the same props,
// but trigger the location change with javascript
export function PopoverLink({
  href,
  target = '',
  rel = '',
  children,
}: {
  href: string;
  target?: string;
  rel?: string;
  children: React.ReactNode;
}) {
  return (
    <a href={href} target={target} rel={rel}>
      {children}
    </a>
  );
}

export interface TextInputProps<T> extends Omit<InputProps, 'onChange' | 'size' | 'value'> {
  value?: T;
  hint?: React.ReactNode;
  label?: React.ReactNode;
  onChange?: (arg0: T, arg1?: boolean) => void;
  onDetailsChange?: (arg0: { value: T; name: string }, arg1?: boolean) => void;
  groupStyle?: CSSProperties;
  rows?: number;
}

export function FormInput<T extends string | number | Array<string> | undefined = string>({
  type = 'text',
  hint,
  label,
  onChange = noop,
  onDetailsChange = noop,
  required = false,
  disabled = false,
  className = '',
  style = {},
  groupStyle = {},
  ...props
}: TextInputProps<T>) {
  return (
    <div style={groupStyle}>
      <Flex gap={5} align="center">
        {createLabel(label, required, hint)}
      </Flex>
      <Input
        className={className}
        type={type}
        onChange={({ target }) => {
          const {
            validity: { valid },
            value,
            name,
          } = target;
          if (value != null) onChange(value as unknown as T, valid);
          if (value != null) onDetailsChange({ value: value as unknown as T, name }, valid);
        }}
        required={required}
        disabled={disabled}
        style={style}
        {...props}
      />
    </div>
  );
}

export function FormNumberInput(props: TextInputProps<string>): React.ReactElement {
  return (
    <FormInput
      type="number"
      {...props}
      value={props.value?.toString() || ''}
      onChange={(newValue: string, valid) => {
        const { pattern } = props;
        const isPatternCheckValid = pattern == null || new RegExp(pattern).test(newValue);
        props.onChange?.(newValue, isPatternCheckValid && valid);
      }}
    />
  );
}

interface FormTextAreaProps extends Omit<TextAreaProps, 'onChange' | 'size' | 'value'> {
  value?: string;
  hint?: React.ReactNode;
  label?: React.ReactNode;
  onChange?: (arg0: string) => void;
  required?: boolean;
  groupStyle?: CSSProperties;
  maxLength?: number;
}

export function FormTextarea({
  hint,
  label,
  value = '',
  onChange = noop,
  required = false,
  groupStyle = {},
  maxLength,
  ...props
}: FormTextAreaProps) {
  const [letterCount, setLetterCount] = useState(value.length);

  return (
    <div style={groupStyle}>
      <div style={{ display: 'flex', flexDirection: 'row' }}>
        {createLabel(label, required, hint)}
      </div>
      <Input.TextArea
        className="form-control"
        onChange={(event) => {
          setLetterCount(event.target.value.length);
          onChange(event.target.value);
        }}
        required={required}
        value={value}
        maxLength={maxLength}
        {...props}
      />
      {maxLength && (
        <span style={{ fontSize: '80%' }}>{maxLength - letterCount} characters left</span>
      )}
    </div>
  );
}

export function FormInputTime({
  value,
  onChange,
  ...props
}: {
  value: number;
  onChange: (arg0: number) => void;
}) {
  const formInput = (
    <BlurFormInput
      type="text"
      value={value != null && value === -1 ? '-1' : value != null ? formatTimeDuration(value) : ''}
      step={1}
      onChange={(newValue) =>
        newValue !== '' && newValue === '-1'
          ? onChange(-1)
          : newValue !== '' && Number.isNaN(timeDurationToMilliseconds(newValue))
            ? toastError('The format for the manual result have to be hh:mm:ss.')
            : onChange(timeDurationToMilliseconds(newValue))
      }
      {...props}
    />
  );

  return formInput;
}

interface SelectProps<T extends string | number | null | undefined> extends AntdSelectProps<T> {
  hint?: string | React.ReactNode;
  label?: string | React.ReactNode;
  value?: T;
  children: Array<React.ReactElement<HTMLOptionElement> | null | undefined | boolean>;
  containerStyle?: CSSProperties;
  required?: boolean;
}

// TODO: Replace with ant design select component
export const FormSelect = <T extends string | number | null | undefined>({
  children,
  hint,
  label,
  value,
  required,
  onChange = noop,
  id,
  containerStyle = {},
  ...props
}: SelectProps<T>) => {
  const validChildren = children
    .filter(isDefined)
    .filter(isNotNull)
    .filter(isNoBoolean)
    .filter((c) => c.props != null);
  let options: Array<DefaultOptionType> = [];
  if (isArray(children)) {
    options = validChildren.map((c) => ({
      value: c.props.value as T,
      label: String(c.props.children),
    }));
  }
  const choosedValue = options.find((o) => o.value === value)?.value as T;

  return (
    <div style={{ width: '100%', marginBottom: '1rem', ...containerStyle }}>
      <Flex gap={5}>{createLabel(label, required, hint)}</Flex>
      <Select<T>
        id={id}
        onChange={onChange}
        options={options}
        value={choosedValue}
        style={{ width: '100%' }}
        {...props}
      />
    </div>
  );
};

interface MultiSelectProps extends Omit<AntdSelectProps, 'onChange'> {
  label?: string;
  required?: boolean;
  children: Array<React.ReactElement<HTMLOptionElement>>;
  value: Array<string> | Immutable<Array<string>>;
  onChange: (newValues: Array<string>) => void;
  hint?: React.ReactNode;
  containerStyle?: CSSProperties;
}

export function FormMultiSelect({
  value,
  label,
  onChange = noop,
  required = false,
  children,
  hint,
  containerStyle = {},
  ...props
}: MultiSelectProps) {
  const options = children
    .filter((c) => c != null)
    .map((c) => ({
      value: c.props.value,
      label: String(c.props.children),
    }));
  const choosedValues = options.filter((o) => value?.includes(o.value)).map((o) => o.value);

  return (
    <div style={{ width: '100%', marginBottom: '1rem', ...containerStyle }}>
      <div style={{ display: 'flex', flexDirection: 'row' }}>
        {createLabel(label, required, hint)}
      </div>
      <Select<Array<string>>
        mode="multiple"
        allowClear
        options={options}
        className="basic-multi-select"
        onChange={onChange}
        value={choosedValues}
        style={{ width: '100%' }}
        {...props}
      />
    </div>
  );
}

interface FormCheckProps {
  onChange?: (checked: boolean) => void;
  value: boolean | undefined;
  required?: boolean;
  label?: React.ReactNode | string;
  labelstyle?: CSSProperties;
  hint?: React.ReactNode;
  type?: 'checkbox' | 'switch';
  id?: string;
  disabled?: boolean;
}

export function FormCheck({
  onChange = noop,
  value,
  required = false,
  label,
  labelstyle,
  hint,
  type = 'switch',
  disabled = false,
  id,
}: FormCheckProps) {
  const labelWithRequiredFlag = (
    <div style={labelstyle}>
      <Space size={3}>
        {label}
        {required ? '*' : ''}
        {hint}
      </Space>
    </div>
  );

  if (type === 'switch') {
    const handleChange: SwitchChangeEventHandler = (checked: boolean) => {
      onChange(checked);
    };

    return (
      <Flex gap={5} align="center">
        <Switch
          checked={value ?? false}
          size="small"
          onChange={handleChange}
          id={id}
          disabled={disabled}
        />
        {labelWithRequiredFlag}
      </Flex>
    );
  }

  const handleChange = (event: CheckboxChangeEvent) => {
    onChange(!!event.target.checked);
  };
  return (
    <Checkbox
      id={id}
      checked={value ?? false}
      onChange={handleChange}
      required={required}
      disabled={disabled}
    >
      {labelWithRequiredFlag}
    </Checkbox>
  );
}

export interface BlurFormInputProps<T> extends TextInputProps<T> {
  onChange?: (newValue: T, valid?: boolean) => void;
  value?: T;
  onBlur?: FocusEventHandler<HTMLInputElement>;
  children: React.ReactElement;
}

export const BlurFormInputWrapper = <T,>({
  children,
  onChange,
  value,
  ...rest
}: BlurFormInputProps<T>) => {
  const [intermediateValue, setIntermediateValue] = useState<T | undefined>(value);
  const [isValid, setValidity] = useState<boolean>(true);

  const handleValueChange = (newValue?: T, valid = true) => {
    setIntermediateValue(newValue);
    setValidity(valid);
  };

  useEffect(() => {
    handleValueChange(value, true);
  }, [value]);

  return (
    <>
      {React.cloneElement(children, {
        ...rest,
        onChange: (newValue: T, valid = true) => {
          handleValueChange(newValue, valid);
        },
        onBlur: () => {
          if (!isValid) {
            setIntermediateValue(value);
            return toastError('Your input is invalid. Please change!');
          }
          if (onChange != null && intermediateValue != null) {
            onChange(intermediateValue, isValid);
          }
        },
        value: intermediateValue,
      })}
    </>
  );
};

export const BlurFormInput = <T extends string | number | null | undefined = string>(
  props: TextInputProps<T>,
) => (
  <BlurFormInputWrapper<T> {...props}>
    <FormInput />
  </BlurFormInputWrapper>
);

export const BlurFormTextarea = <_T extends string | null | undefined = string>(
  props: TextInputProps<string>,
) => (
  <BlurFormInputWrapper<string> {...props}>
    <FormTextarea />
  </BlurFormInputWrapper>
);

export const BlurFormNumberInput = <_T extends string | null | undefined = string>(
  props: TextInputProps<string>,
) => (
  <BlurFormInputWrapper<string> {...props}>
    <FormNumberInput />
  </BlurFormInputWrapper>
);

export function FormInputUrl(props: TextInputProps<string>) {
  const { onChange = noop, ...rest } = props;

  function makeUrl(value: string) {
    if (value) {
      if (value.match(/^https?:\/\//)) {
        return value;
      }
      return `http://${value}`;
    }
    return value;
  }

  return <BlurFormInput onChange={(newValue: string) => onChange(makeUrl(newValue))} {...rest} />;
}

export const FullWidthSegmented = styled(Segmented)`
  &.ant-segmented.ant-segmented-block .ant-segmented-item {
    min-width: 0;
    margin-bottom: 0;
  }
`;

function noop() {}
